import React, { useEffect, useRef, useState } from 'react';
import styles from './ContactPage.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMapMarkerAlt,
  faEnvelope,
  faPhoneAlt,
  faClock
} from '@fortawesome/free-solid-svg-icons';
import {
  faInstagram
} from '@fortawesome/free-brands-svg-icons';

// --- FIREBASE IMPORTS ---
import { db } from '../../firebase'; // Adjust this path to match your file structure
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

const ContactPage = () => {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  // --- FORM STATE ---
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // --- INPUT HANDLER ---
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // --- SUBMIT: Firestore write client-side, then mail via API ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // 1. Store lead in Firestore ("contactSubmissions" matches your Leads code)
      const docRef = await addDoc(collection(db, 'contactSubmissions'), {
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        service: "General Inquiry", // Default service tag
        status: "new",              // Default status for your Leads table
        createdAt: serverTimestamp() // Required for your dashboard sorting
      });

      // 2. Trigger notification mail — a mail failure shouldn't fail the lead
      try {
        await fetch("/api/contact", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...formData, docId: docRef.id }),
        });
      } catch (mailErr) {
        console.error("Mail notification failed (lead saved):", mailErr);
      }

      alert("Message Sent!");

      // Clear form after success
      setFormData({ fullName: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error("Error saving lead: ", error);
      alert("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main ref={sectionRef} className={`${styles.wrapper} ${visible ? styles.show : ""}`}>
      <div className={styles.container}>

        {/* Left Side: Info */}
        <div className={styles.infoSection}>
          <h1 className={styles.mainTitle}>Get In Touch</h1>
          <p className={styles.description}>
           Whether you’re planning an intimate dinner, a larger celebration, or simply looking to experience an evening at Baglami, our team is here to assist. From reservations and private events to collaborations and general enquiries, we’re always happy to connect and help curate an experience that feels seamless, thoughtful, and tailored to the occasion.
          </p>

          <div className={styles.contactGrid}>
            {[
              { icon: faMapMarkerAlt, text: ["Bağlami, Bombay @ BKC 7, Ins Tower, Opp NMACC, Bandra Kurla Complex, Mumbai, Maharashtra 400098"] },
              { icon: faPhoneAlt, text: ["8879199982", "8879199984"], underline: true },
              { icon: faEnvelope, text: ["info@baglami.com"] },
              { icon: faClock, text: ["12pm – 4 pm", "7 pm – 1:30 am"] }
            ].map((item, idx) => (
              <div key={idx} className={styles.contactItem} style={{ transitionDelay: `${idx * 100}ms` }}>
                <FontAwesomeIcon icon={item.icon} className={styles.icon} />
                <div>
                  {item.text.map((t, i) => (
                    <p key={i} className={item.underline ? styles.underline : ""}>{t}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className={styles.socials}>
            <h3>Follow Us</h3>
            <div className={styles.socialIcons}>
              <a target="_blank" rel="noreferrer" href="https://www.instagram.com/baglamibombay/"><FontAwesomeIcon icon={faInstagram} /></a>
            </div>
          </div>
        </div>

        {/* Right Side: Form Card */}
        <div className={styles.formCard}>
          <h2 className={styles.formTitle}>Your Details</h2>
          <p className={styles.formSubtitle}>Let us know how to get back to you.</p>

          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.inputGroup}>
              <label>Your Name</label>
              <input 
                type="text" 
                name="fullName"
                placeholder="Ex. John Doe" 
                required 
                value={formData.fullName}
                onChange={handleChange}
              />
            </div>

            <div className={styles.inputGroup}>
              <label>Your Email</label>
              <input 
                type="email" 
                name="email"
                placeholder="example@mail.com" 
                required 
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            <div className={styles.inputGroup}>
              <label>Your Phone</label>
              <input 
                type="tel" 
                name="phone"
                placeholder="+91 00000 00000" 
                required 
                value={formData.phone}
                onChange={handleChange}
              />
            </div>

            <div className={styles.inputGroup}>
              <label>Your Message</label>
              <textarea 
                className={styles.textarea} 
                name="message"
                placeholder="How can we help you?" 
                required 
                rows={4} 
                value={formData.message}
                onChange={handleChange}
              />
            </div>

            <div className={styles.btnWrapper}>
              <button 
                type="submit" 
                className={styles.submitBtn} 
                disabled={isSubmitting}
              >
                {isSubmitting ? "Sending..." : "Submit Application"}
              </button>
            </div>
          </form>
        </div>

      </div>
    </main>
  );
};

export default ContactPage;